import {ActionTree, GetterTree, Module, MutationTree} from 'vuex';
import {RootState} from '../RootState';
import {settings} from '@/plugins/settings';
import {helpdeskAxios} from '@/plugins/helpdeskAxios';
import responseHandler from '@/plugins/errorHandling';
import {
  EWhatsappConversationStatus, IWhatsappConversation, IWhatsappConversationFilters, IWhatsappMessage,
  IWhatsappStatus, IWhatsappTemplate, IWhatsappTemplateSend,
} from '@/model/Helpdesk/IWhatsapp';

// WhatsApp chats of the helpdesk (Meta Cloud API behind the helpdesk backend).
// User actions go through responseHandler (OK/KO events + toasts). Polling (new messages, list
// refresh) returns plain promises and fails silently: a toast every few seconds would be noise.

const BASE = () => settings.helpdeskEndpoint + 'v1/whatsapp/';
const MESSAGES_PAGE = 50;

interface WhatsappState {
  status: IWhatsappStatus | null;
  conversations: IWhatsappConversation[];
  currentPage: number;
  lastPage: number;
  total: number;
  perPage: number;
  selected: IWhatsappConversation | null;
  messages: IWhatsappMessage[];
  // false once a "before_id" page comes back short: the conversation has no older messages.
  hasOlder: boolean;
  templates: IWhatsappTemplate[];
  errors: any;
}

const state: WhatsappState = {
  status: null,
  conversations: [],
  currentPage: 1,
  lastPage: 1,
  total: 0,
  perPage: 30,
  selected: null,
  messages: [],
  hasOlder: false,
  templates: [],
  errors: {},
};

const getters: GetterTree<WhatsappState, RootState> = {
  status:        (state) => state.status,
  conversations: (state) => state.conversations,
  currentPage:   (state) => state.currentPage,
  lastPage:      (state) => state.lastPage,
  total:         (state) => state.total,
  perPage:       (state) => state.perPage,
  selected:      (state) => state.selected,
  messages:      (state) => state.messages,
  hasOlder:      (state) => state.hasOlder,
  templates:     (state) => state.templates,
  errors:        (state) => state.errors,
};

// Keeps the list row and the open conversation in sync after any change from the server.
const replaceConversation = (state: WhatsappState, conversation: IWhatsappConversation) => {
  const idx = state.conversations.findIndex((c) => c.id === conversation.id);
  if (idx !== -1) state.conversations.splice(idx, 1, conversation);
  if (state.selected && state.selected.id === conversation.id) state.selected = conversation;
};

// Appends without duplicates: a sent message can come back again in the next poll.
const appendMessages = (state: WhatsappState, messages: IWhatsappMessage[]) => {
  messages.forEach((message) => {
    const idx = state.messages.findIndex((m) => m.id === message.id);
    if (idx === -1) state.messages.push(message);
    else state.messages.splice(idx, 1, message);
  });
};

const mutations: MutationTree<WhatsappState> = {
  errors(state, payload: any) { state.errors = payload; },
  whatsappStatusLoaded(state, payload: IWhatsappStatus) { state.status = payload; },
  whatsappConversationsLoaded(state, payload: any) {
    state.conversations = payload.data;
    state.currentPage   = payload.current_page;
    state.lastPage      = payload.last_page;
    state.total         = payload.total;
    state.perPage       = payload.per_page;
  },
  whatsappConversationSelected(state, payload: IWhatsappConversation | null) {
    if (!payload || !state.selected || state.selected.id !== payload.id) {
      state.messages = [];
      state.hasOlder = false;
    }
    state.selected = payload;
  },
  whatsappConversationLoaded(state, payload: IWhatsappConversation) { replaceConversation(state, payload); },
  whatsappConversationUpdated(state, payload: IWhatsappConversation) { replaceConversation(state, payload); },
  whatsappConversationRead(state, payload: IWhatsappConversation) { replaceConversation(state, payload); },
  whatsappConversationStarted(state, payload: IWhatsappConversation) {
    if (!state.conversations.some((c) => c.id === payload.id)) state.conversations.unshift(payload);
    else replaceConversation(state, payload);
  },
  whatsappMessagesLoaded(state, payload: IWhatsappMessage[]) {
    state.messages = payload;
    state.hasOlder = payload.length >= MESSAGES_PAGE;
  },
  whatsappOlderMessagesLoaded(state, payload: IWhatsappMessage[]) {
    state.messages = payload.concat(state.messages.filter((m) => !payload.some((p) => p.id === m.id)));
    state.hasOlder = payload.length >= MESSAGES_PAGE;
  },
  whatsappNewMessagesLoaded(state, payload: IWhatsappMessage[]) { appendMessages(state, payload); },
  whatsappMessageSent(state, payload: IWhatsappMessage) { appendMessages(state, [payload]); },
  whatsappMediaSent(state, payload: IWhatsappMessage) { appendMessages(state, [payload]); },
  whatsappTemplateSent(state, payload: IWhatsappMessage) { appendMessages(state, [payload]); },
  whatsappTemplatesLoaded(state, payload: IWhatsappTemplate[]) { state.templates = payload; },
};

const conversationParams = (filters: IWhatsappConversationFilters) => {
  const query = new URLSearchParams();
  if (filters.page)     query.set('page', String(filters.page));
  if (filters.per_page) query.set('per_page', String(filters.per_page));
  if (filters.search)   query.set('search', filters.search);
  if (filters.assignee) query.set('assignee', filters.assignee);
  (filters.statuses || []).forEach((s: EWhatsappConversationStatus) => query.append('status[]', s));
  const qs = query.toString();
  return qs ? '?' + qs : '';
};

const actions: ActionTree<WhatsappState, RootState> = {
  fetchStatus: ({commit}) => responseHandler.handle(
    () => helpdeskAxios({url: BASE() + 'status', method: 'GET'}),
    commit, 'whatsappStatusLoaded',
  ),

  fetchConversations: ({commit}, filters: IWhatsappConversationFilters = {}) => responseHandler.handle(
    () => helpdeskAxios({url: BASE() + 'conversations' + conversationParams(filters), method: 'GET'}),
    commit, 'whatsappConversationsLoaded',
  ),

  // Silent refresh of the current page (new chats, unread counters, last message).
  pollConversations: ({commit}, filters: IWhatsappConversationFilters = {}) =>
    helpdeskAxios({url: BASE() + 'conversations' + conversationParams(filters), method: 'GET'})
      .then((res: any) => commit('whatsappConversationsLoaded', res.data.data))
      .catch(() => undefined),

  selectConversation: ({commit}, conversation: IWhatsappConversation | null) => {
    commit('whatsappConversationSelected', conversation);
  },

  fetchConversation: ({commit}, id: number) => responseHandler.handle(
    () => helpdeskAxios({url: BASE() + 'conversations/' + id, method: 'GET'}),
    commit, 'whatsappConversationLoaded',
  ),

  // Silent refresh of the open chat (24h window, unread, status), even if the filters hide it.
  pollConversation: ({commit}, id: number) =>
    helpdeskAxios({url: BASE() + 'conversations/' + id, method: 'GET'})
      .then((res: any) => commit('whatsappConversationLoaded', res.data.data))
      .catch(() => undefined),

  updateConversation: ({commit}, {id, data}: {id: number; data: Partial<IWhatsappConversation>}) => responseHandler.handle(
    () => helpdeskAxios({url: BASE() + 'conversations/' + id, method: 'PATCH', data}),
    commit, 'whatsappConversationUpdated',
  ),

  markRead: ({commit}, id: number) =>
    helpdeskAxios({url: BASE() + 'conversations/' + id + '/read', method: 'POST'})
      .then((res: any) => commit('whatsappConversationRead', res.data.data))
      .catch(() => undefined),

  fetchMessages: ({commit}, id: number) => responseHandler.handle(
    () => helpdeskAxios({url: BASE() + 'conversations/' + id + '/messages', method: 'GET', params: {limit: MESSAGES_PAGE}}),
    commit, 'whatsappMessagesLoaded',
  ),

  fetchOlderMessages: ({commit}, {id, beforeId}: {id: number; beforeId: number}) => responseHandler.handle(
    () => helpdeskAxios({
      url: BASE() + 'conversations/' + id + '/messages', method: 'GET', params: {before_id: beforeId, limit: MESSAGES_PAGE},
    }),
    commit, 'whatsappOlderMessagesLoaded',
  ),

  // Messages newer than the last one shown, plus older ones whose status changed (sent → delivered
  // → read) since the newest updated_at we have. Some come back repeated: appendMessages merges by id.
  pollMessages: ({commit, state}, id: number) => {
    const last = state.messages.length ? state.messages[state.messages.length - 1] : null;
    const params: any = {limit: 100};
    if (last) {
      params.after_id = last.id;
      params.updated_since = state.messages.reduce((max, m) => (m.updated_at > max ? m.updated_at : max), last.updated_at);
    }
    return helpdeskAxios({url: BASE() + 'conversations/' + id + '/messages', method: 'GET', params})
      .then((res: any) => {
        if (state.selected && state.selected.id === id) commit('whatsappNewMessagesLoaded', res.data.data);
      })
      .catch(() => undefined);
  },

  sendText: ({commit}, {id, body}: {id: number; body: string}) => responseHandler.handle(
    () => helpdeskAxios({url: BASE() + 'conversations/' + id + '/messages', method: 'POST', data: {body}}),
    commit, 'whatsappMessageSent',
  ),

  sendMedia: ({commit}, {id, file, caption}: {id: number; file: File; caption?: string}) => {
    const data = new FormData();
    data.append('file', file);
    if (caption) data.append('caption', caption);
    return responseHandler.handle(
      () => helpdeskAxios({url: BASE() + 'conversations/' + id + '/media', method: 'POST', data}),
      commit, 'whatsappMediaSent',
    );
  },

  sendTemplate: ({commit}, {id, template}: {id: number; template: IWhatsappTemplateSend}) => responseHandler.handle(
    () => helpdeskAxios({url: BASE() + 'conversations/' + id + '/template', method: 'POST', data: template}),
    commit, 'whatsappTemplateSent',
  ),

  startConversation: ({commit}, data: {phone: string; name?: string; template: IWhatsappTemplateSend}) => responseHandler.handle(
    () => helpdeskAxios({url: BASE() + 'conversations', method: 'POST', data}),
    commit, 'whatsappConversationStarted',
  ),

  fetchTemplates: ({commit}) => responseHandler.handle(
    () => helpdeskAxios({url: BASE() + 'templates', method: 'GET'}),
    commit, 'whatsappTemplatesLoaded',
  ),
};

export const helpdeskWhatsappModule: Module<WhatsappState, RootState> = {
  namespaced: true, state, getters, mutations, actions,
};
